import React, { useState, useEffect } from 'react';
import { Download, CheckCircle } from 'lucide-react';

const FinalPDF = ({ onSubmit, onNext, onPrevious, initialData = {} }) => {
  const [watermark, setWatermark] = useState(initialData.watermark || 'none');
  const [redactions, setRedactions] = useState(initialData.redactions || []);
  const [pageLabels, setPageLabels] = useState(initialData.pageLabels || true);
  const [language, setLanguage] = useState(initialData.language || 'en');
  const [digitalSignature, setDigitalSignature] = useState(initialData.digitalSignature || {
    enabled: false,
    signer: '',
    certificateAlias: '',
    timestampServer: '',
    appearance: 'lastPage'
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [pdfGenerated, setPdfGenerated] = useState(initialData.pdfGenerated || false);
  const [pdfInfo, setPdfInfo] = useState(initialData.pdfInfo || null);

  useEffect(() => {
    // Load initial data when component mounts
  }, [initialData]);

  const addRedaction = () => {
    const newRedaction = {
      id: Date.now(),
      page: 1,
      x: 0,
      y: 0,
      width: 100,
      height: 20,
      reason: 'Confidential information'
    };
    setRedactions([...redactions, newRedaction]);
  };

  const removeRedaction = (id) => {
    setRedactions(redactions.filter(r => r.id !== id));
  };

  const updateRedaction = (id, field, value) => {
    setRedactions(redactions.map(r => 
      r.id === id ? { ...r, [field]: value } : r
    ));
  };

  const generatePDF = async () => {
    setIsGenerating(true);
    
    // Simulate PDF generation
    await new Promise(resolve => setTimeout(resolve, 3000));
    
    const pdfData = {
      filename: 'report_v1_final.pdf',
      checksum: 'a1b2c3d4e5f6...',
      size: '2.4 MB',
      pages: 45,
      generatedAt: new Date().toISOString()
    };
    
    setPdfInfo(pdfData);
    setPdfGenerated(true);
    setIsGenerating(false);
  };


  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Final PDF Generation</h2>
        <p className="text-gray-600">Generate the final report PDF with your chosen settings</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column - Settings */}
        <div className="space-y-6">
          {/* Watermark Options */}
          <div>
            <h3 className="text-lg font-semibold text-gray-700 mb-3">Watermark</h3>
            <div className="space-y-2">
              {['none', 'confidential', 'forWeb'].map(option => (
                <label key={option} className="flex items-center">
                  <input
                    type="radio"
                    name="watermark"
                    value={option}
                    checked={watermark === option}
                    onChange={(e) => setWatermark(e.target.value)}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700 capitalize">
                    {option === 'none' ? 'None (default)' : 
                     option === 'confidential' ? 'Confidential' : 'For web'}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Redactions */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-semibold text-gray-700">Redactions</h3>
              <button
                onClick={addRedaction}
                className="text-sm text-blue-500 hover:text-blue-600"
              >
                + Add Redaction
              </button>
            </div>
            {redactions.length === 0 ? (
              <p className="text-sm text-gray-500">No redactions configured</p>
            ) : (
              <div className="space-y-3">
                {redactions.map((redaction) => (
                  <div key={redaction.id} className="p-3 border border-gray-200 rounded-lg">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <input
                        type="number"
                        value={redaction.page}
                        onChange={(e) => updateRedaction(redaction.id, 'page', parseInt(e.target.value))}
                        placeholder="Page"
                        className="px-2 py-1 border border-gray-300 rounded"
                      />
                      <input
                        type="text"
                        value={redaction.reason}
                        onChange={(e) => updateRedaction(redaction.id, 'reason', e.target.value)}
                        placeholder="Reason"
                        className="px-2 py-1 border border-gray-300 rounded"
                      />
                    </div>
                    <button
                      onClick={() => removeRedaction(redaction.id)}
                      className="text-red-500 text-xs mt-2"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Page Labels & TOC */}
          <div>
            <h3 className="text-lg font-semibold text-gray-700 mb-3">Page Labels & TOC</h3>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={pageLabels}
                onChange={(e) => setPageLabels(e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Refresh page labels and table of contents</span>
            </label>
          </div>

          {/* Language */}
          <div>
            <h3 className="text-lg font-semibold text-gray-700 mb-3">Language</h3>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="en">English</option>
              <option value="de">German</option>
              <option value="fr">French</option>
              <option value="es">Spanish</option>
            </select>
          </div>

          {/* Digital Signature */}
          <div>
            <div className="flex items-center mb-3">
              <input
                type="checkbox"
                checked={digitalSignature.enabled}
                onChange={(e) => setDigitalSignature(prev => ({ ...prev, enabled: e.target.checked }))}
                className="mr-2"
              />
              <h3 className="text-lg font-semibold text-gray-700">Digital Signature (Optional)</h3>
            </div>
            
            {digitalSignature.enabled && (
              <div className="space-y-3 ml-6">
                <input
                  type="text"
                  value={digitalSignature.signer}
                  onChange={(e) => setDigitalSignature(prev => ({ ...prev, signer: e.target.value }))}
                  placeholder="Signer: org common name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
                <input
                  type="text"
                  value={digitalSignature.certificateAlias}
                  onChange={(e) => setDigitalSignature(prev => ({ ...prev, certificateAlias: e.target.value }))}
                  placeholder="Certificate alias"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
                <input
                  type="text"
                  value={digitalSignature.timestampServer}
                  onChange={(e) => setDigitalSignature(prev => ({ ...prev, timestampServer: e.target.value }))}
                  placeholder="Timestamp server (optional)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
                <select
                  value={digitalSignature.appearance}
                  onChange={(e) => setDigitalSignature(prev => ({ ...prev, appearance: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="lastPage">Signature block on last page</option>
                  <option value="custom">Custom position</option>
                </select>
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Generation */}
        <div className="space-y-6">
          {/* PDF Info */}
          {pdfGenerated && pdfInfo && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center mb-2">
                <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                <h3 className="font-semibold text-green-800">PDF Generated Successfully</h3>
              </div>
              <div className="space-y-1 text-sm text-green-700">
                <p><strong>Filename:</strong> {pdfInfo.filename}</p>
                <p><strong>Size:</strong> {pdfInfo.size}</p>
                <p><strong>Pages:</strong> {pdfInfo.pages}</p>
                <p><strong>Checksum:</strong> {pdfInfo.checksum}</p>
                <p><strong>Generated:</strong> {new Date(pdfInfo.generatedAt).toLocaleString()}</p>
              </div>
              <button className="mt-3 flex items-center text-green-600 hover:text-green-700">
                <Download className="w-4 h-4 mr-1" />
                Download PDF
              </button>
            </div>
          )}

        </div>
      </div>

      {/* Generate Button - Bottom Right */}
      <div className="flex justify-end mt-6">
        <button
          onClick={generatePDF}
          disabled={isGenerating}
          className={`px-6 py-3 rounded-md transition-colors ${
            isGenerating
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-blue-500 text-white hover:bg-blue-600'
          }`}
        >
          {isGenerating ? 'Generating PDF...' : 'Generate Final PDF'}
        </button>
      </div>

    </div>
  );
};

export default FinalPDF;
